// App
import { useDispatch, useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';

// Lodash
import assign from 'lodash/assign';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';

// Actions
import AdActions from 'app/shared/flux/actions/AdActions';

// Helpers
import {
  getAdvertisementConfigurationsHelper,
  getReduxAdSlotHelper,
  getStateForAdvertisementHelper,
  getTargeting,
  isAdBlockedOrEmpty,
} from 'app/shared/modules/AdvertisementWrapper/helpers';

// Misc / utils
import { DEFAULT } from 'app/shared/models/Filter';
import queryUtils from 'app/shared/utils/queryUtils';
import genericUtils from 'app/shared/utils/genericUtils';
import isoConfig from 'config/isoConfig';
import { getGlobalLogger } from '@zg-rentals/logger-base';

const logger = getGlobalLogger('modules/useads');

type AdConfiguration = {
  name: string;
  index: number;
  placement: string;
  format: string;
  sizes: unknown;
  customTargeting?: Record<string, unknown>;
  type: string;
};

type UseAdsProps = {
  handleHideAdElement: (slot: 'slot1' | 'slot2', hidden: boolean) => void;
  filter: typeof DEFAULT;
  registeredConfigurations: Array<AdConfiguration>;
  registeredIds: Array<string>;
};

export const useAds = ({ handleHideAdElement, filter, registeredConfigurations, registeredIds }: UseAdsProps) => {
  // Redux
  const dispatch = useDispatch();
  const ads = useSelector((state: RootReduxState) => state.ads);
  const area = useSelector((state: RootReduxState) => state.area);
  const gadsInit = useSelector((state: RootReduxState) => state.ads.gadsInit);
  const query = useSelector((state: RootReduxState) => state.location.current.query);
  const userToken = useSelector((state: RootReduxState) => state.user.userToken);
  const searchListings = useSelector((state: RootReduxState) => state.listings.listingGroups.byCoords);
  const advertisementEnv = useSelector((state: RootReduxState) => state.app.advertisementEnv);
  const useStageForTargeting =
    advertisementEnv === 'local' ||
    advertisementEnv === 'comet1' ||
    advertisementEnv === 'sherman' ||
    advertisementEnv === 'feeds' ||
    advertisementEnv === 'node-int';

  const _checkIfAdsShouldBeHidden = () => {
    if (isAdBlockedOrEmpty(registeredIds?.[0])) {
      handleHideAdElement('slot1', true);
    } else {
      handleHideAdElement('slot1', false);
    }

    if (isAdBlockedOrEmpty(registeredIds?.[1])) {
      handleHideAdElement('slot2', true);
    } else {
      handleHideAdElement('slot2', false);
    }
  };

  const _getAdTargeting = () => {
    const defaultFilter = DEFAULT;

    return getTargeting({ defaultFilter, filter, area, searchListings });
  };

  const defineAd = async (forceRefreshSlots?: boolean, forceDestroySlots?: boolean) => {
    let refresh = forceRefreshSlots;
    let [definedAdSlot1, definedAdSlot2] = getReduxAdSlotHelper({ adConfigs: registeredConfigurations, ads });

    if (gadsInit) {
      const isMapBorderActive = queryUtils.isMapBorderActive(query);
      const areaState = area?.area?.state?.toLowerCase();
      const googletag = window.googletag;
      const publisherId = isoConfig.googlePublisherId;
      const preFetchPromise = (await dispatch(
        AdActions.adTags({
          aliasEncoded: undefined,
          maxLat: area.area.maxLat,
          minLat: area.area.minLat,
          maxLon: area.area.maxLon,
          minLon: area.area.minLon,
          lowPrice: filter.price.min,
          highPrice: filter.price.max,
        }),
      )) as { attributes?: Record<string, string> };
      const attributes = preFetchPromise?.attributes;
      const adTagsResponse: { dma?: string; cnty?: string; state?: string; prange?: string } = {};
      if (attributes) {
        adTagsResponse.dma = attributes.dma;
        adTagsResponse.cnty = attributes.cnty;
        /**
         * Use state found in area reducer if the map has a definend boundary AND areaState is truthy
         * else, fall back on the adTags fetched state
         */
        adTagsResponse.state = getStateForAdvertisementHelper(isMapBorderActive, areaState, attributes.state);
        adTagsResponse.prange = attributes.prange;
      }

      return new Promise<void>((resolve) => {
        googletag.cmd.push(() => {
          if ((definedAdSlot1 || definedAdSlot2) && !forceDestroySlots) {
            definedAdSlot1.clearTargeting();
            definedAdSlot2.clearTargeting();

            refresh = true;
          } else {
            if ((definedAdSlot1 || definedAdSlot2) && forceDestroySlots) {
              // Destroy ALL
              googletag.forceDestroySlots();
            }

            // Create ads for the first time
            let adUnit1 = `/${publisherId}/${registeredConfigurations[0].placement}/`;
            let adUnit2 = `/${publisherId}/${registeredConfigurations[1].placement}/`;

            adUnit1 += registeredConfigurations[0].format;
            adUnit2 += registeredConfigurations[1].format;

            definedAdSlot1 = googletag.defineSlot(adUnit1, registeredConfigurations[0].sizes, registeredIds[0]);
            definedAdSlot2 = googletag.defineSlot(adUnit2, registeredConfigurations[1].sizes, registeredIds[1]);
          }
          const customTargeting1 = assign({}, _getAdTargeting(), registeredConfigurations[0].customTargeting);
          const customTargeting2 = assign({}, _getAdTargeting(), registeredConfigurations[1].customTargeting);

          if (customTargeting1) {
            forEach(customTargeting1, (val, key) => {
              if (val && (isNumber(val) || !isEmpty(val))) {
                definedAdSlot1.setTargeting(key, val);
              }
            });
          }

          if (customTargeting2) {
            forEach(customTargeting2, (val, key) => {
              if (val && (isNumber(val) || !isEmpty(val))) {
                definedAdSlot2.setTargeting(key, val);
              }
            });
          }

          // adTags api trumps customTargeting/gamAttributes
          if (!isEmpty(adTagsResponse)) {
            forEach(adTagsResponse, (v, k) => {
              definedAdSlot1.setTargeting(k, v);
              definedAdSlot2.setTargeting(k, v);
            });
          }

          // Set targeting env to 'stage' if we're in any environment other than prod
          if (useStageForTargeting) {
            definedAdSlot1.setTargeting('env', 'stage');
            definedAdSlot2.setTargeting('env', 'stage');
          } else {
            // If production env or production-docker
            definedAdSlot1.setTargeting('env', 'production');
            definedAdSlot2.setTargeting('env', 'production');
          }

          definedAdSlot1.setTargeting('vers', 'hp_search_control');
          definedAdSlot2.setTargeting('vers', 'hp_search_control');

          definedAdSlot1.setTargeting('zguidh', genericUtils.hash(userToken));
          definedAdSlot2.setTargeting('zguidh', genericUtils.hash(userToken));

          // if it is our first time defining the slot, add the googletag.pubads() service
          if (!refresh || forceDestroySlots) {
            definedAdSlot1.addService(googletag.pubads());
            definedAdSlot2.addService(googletag.pubads());
          }

          googletag.pubads().set('page_url', window.location.href);
          const definedAdSlots = [definedAdSlot1, definedAdSlot2];
          definedAdSlots.map((adSlot, idx) => {
            dispatch(
              AdActions.setAdSlot(registeredConfigurations[idx].type, {
                id: registeredIds[idx],
                slot: adSlot,
                shouldRefresh: false,
              }),
            );
          });
          resolve();
        });
      })
        .then(() => {
          googletag.cmd.push(() => {
            if (document.getElementById(registeredIds[0])) {
              if (!forceDestroySlots && refresh && registeredIds[0]) {
                googletag.pubads().refresh([definedAdSlot1, definedAdSlot2]);
              } else {
                googletag.display(registeredIds[0]);
              }
            } else {
              logger?.warn('Ad container div not yet rendered, should not display. %s', registeredIds[0]);
            }
          });
          return undefined;
        })
        .then(() => {
          //wait 700ms before checking to see if the ad rendered
          return new Promise<void>((resolve) => setTimeout(() => resolve(), 700));
        })
        .then(() => {
          // Check if any ads are blocked or empty
          _checkIfAdsShouldBeHidden();
          return undefined;
        });
    }
  };

  const refreshAd = ([oldPlacement1, oldPlacement2]: [string, string]) => {
    const newAdConfigs = getAdvertisementConfigurationsHelper(filter);
    const isAdPlacement1Different = oldPlacement1 && !isEqual(oldPlacement1, newAdConfigs[0].placement);
    const isAdPlacement2Different = oldPlacement2 && !isEqual(oldPlacement2, newAdConfigs[1].placement);
    let mustDestroySlots;

    if (isAdPlacement1Different || isAdPlacement2Different) {
      mustDestroySlots = true;
    }

    return defineAd(true, mustDestroySlots).then(() => {
      // Check if any ads are blocked or empty
      _checkIfAdsShouldBeHidden();
      return undefined;
    });
  };

  const cleanUpAd = () => {
    if (window.googletag && window.googletag.pubads && window.googletag.pubads() && window.googletag.pubads().clear) {
      window.googletag.pubads().clear();
    }
  };

  return [defineAd, refreshAd, cleanUpAd];
};
